import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Chip,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { xsHeight, mdHeight } from "components/AppBar";
import { tabsOffset } from "components/TabLinks";
import { FeatureFlagOverride } from "generated-graphql/graphql";
import { useMemo } from "react";

import { useFacility } from "../useFacility";
import { V2DisplayOnlyField as DisplayOnlyField } from "./V2DisplayOnlyField";

export const BASICS_SECTION_ID = "basics";
export const BASICS_SECTION_ICON = "🏭";

export const BasicsSection: React.FC = () => {
  const theme = useTheme();
  const { data } = useFacility();
  const facility = data?.facility;

  const enabledPrograms = useMemo(() => {
    const programs: string[] = [];
    if (facility?.chemicalFeatureFlagOverride !== FeatureFlagOverride.Disabled) {
      programs.push("Chemicals");
    }
    if (facility?.wasteFeatureFlagOverride !== FeatureFlagOverride.Disabled) {
      programs.push("Waste");
    }
    return programs;
  }, [facility?.chemicalFeatureFlagOverride, facility?.wasteFeatureFlagOverride]);

  const sortedGovernmentIds = useMemo(() => {
    const ids = facility?.facilityAlternateIds ?? [];
    return [...ids].sort((a, b) => (a.type ?? "").localeCompare(b.type ?? ""));
  }, [facility?.facilityAlternateIds]);

  const tags = useMemo(() => {
    return facility?.tags?.map((t) => t.name) ?? [];
  }, [facility?.tags]);

  const colocatedDisplay = useMemo(() => {
    if (!facility?.colocationReportingFacility) return "No";
    return `Yes — ${facility.colocationReportingFacility.name}`;
  }, [facility?.colocationReportingFacility]);

  return (
    <Box
      id={BASICS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
        "& .edit-icon": { opacity: 0, transition: "opacity 200ms" },
        "&:hover .edit-icon": { opacity: 1 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">{BASICS_SECTION_ICON} Basics</Typography>
        <IconButton size="small" className="edit-icon" sx={{ color: "action.selected", mr: 2 }}>
          <Edit />
        </IconButton>
      </Stack>

      {/* Facility Name — full width */}
      <Box mb={2}>
        <DisplayOnlyField label="Facility Name" text={facility?.name ?? ""} />
      </Box>

      {/* 3-column grid for the rest */}
      <Grid container columnSpacing={3} rowSpacing={2}>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Facility ID" text={facility?.customerFacilityId ?? ""} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Company Name" text={facility?.companyName ?? ""} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Department" text={facility?.department ?? ""} />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Business Unit" text={facility?.businessUnit ?? ""} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Phone Number" text={facility?.phone ?? ""} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="24-Hour Phone Number" text={facility?.phone24Hour ?? ""} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Dun & Bradstreet Number" text={facility?.dbNumber ?? ""} />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="NAICS Code" text={facility?.naicsCode?.toString() ?? ""} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Facility Type" text={facility?.facilityType ?? ""} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Facility Status" text={facility?.status ?? ""} />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Facility Status Reason" text={facility?.statusReason ?? ""} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Date Opened" text={facility?.dateOpened ?? ""} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Max Occupancy"
            text={facility?.maxNumOccupants?.toString() ?? ""}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField label="Colocated" text={colocatedDisplay} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Encamp as Billing Contact"
            text={facility?.useEncampBillingContact ? "Yes" : "No"}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Tags"
            renderValue={() => (
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" />
                ))}
              </Stack>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Enabled Programs"
            renderValue={() => (
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {enabledPrograms.map((program) => (
                  <Chip key={program} label={program} size="small" />
                ))}
              </Stack>
            )}
          />
        </Grid>

        {/* Government IDs — sorted alphabetically by type */}
        {sortedGovernmentIds.map((altId) => (
          <Grid item xs={12} sm={6} lg={4} key={altId.id}>
            <DisplayOnlyField
              label={altId.type ?? "Government ID"}
              text={altId.hasNoId ? "No ID" : (altId.value ?? "")}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
